import type { FC } from 'react';
import React, { useEffect } from 'react';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import CheckIcon from '@atlaskit/icon/core/migration/check-mark--check';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import Popup from '@atlaskit/popup';
import { N80, N50, N0, B100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { PRIMARY_COLORS } from '../colors';
import { colorCodeToColorName, tokenToHex } from '../helpers';

import type { ColorPickerButtonProps } from './types';

type DropDownContentWrapperProps = {
	ref?: any;
};

type SelectedColorProps = {
	color: string | undefined;
	isOpen: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedColor = styled.div<SelectedColorProps>({
	width: '16px',
	height: '16px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: `2px solid ${(props: SelectedColorProps) =>
		props.isOpen
			? token('color.border.inverse', N0)
			: props.color
				? props.color
				: token('color.border.accent.gray', N50)}`,
	borderRadius: '22px',
	marginRight: token('space.negative.025', '-2px'),
	marginLeft: token('space.025', '2px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: SelectedColorProps) => props.color,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorOption = styled.button<{ color: string }>({
	width: '20px',
	height: '20px',
	margin: 'auto',
	display: 'flex',
	border: `1px solid ${token('color.border.inverse', N0)}`,
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: '22px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.color,
	outlineColor: token('color.border.brand', B100),
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorOptionWrapper = styled.div({
	cursor: 'pointer',
	flexGrow: 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover button': {
		boxShadow: `0 0 0 0.8px ${token('color.border.bold', N80)}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropDownContentWrapper = styled.div<DropDownContentWrapperProps>({
	display: 'flex',
	justifyContent: 'space-evenly',
	alignItems: 'stretch',
	width: '145px',
	height: '30px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonIconsWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const chevronDownIconWrapperStyles = xcss({
	marginRight: 'space.negative.075',
});

export const ColorPickerButton: FC<ColorPickerButtonProps> = ({
	selectedColor,
	onSelectColor,
	isOpen,
	onClose,
	onColorPickerToggle,
	userOffline,
	colorPickerComponentRef,
}) => {
	const colorsArray = [
		PRIMARY_COLORS.BLUE,
		PRIMARY_COLORS.GREEN,
		PRIMARY_COLORS.YELLOW,
		PRIMARY_COLORS.RED,
		PRIMARY_COLORS.PURPLE,
	];

	const statusColors = colorsArray.map((color) => {
		const isSelected = selectedColor === color;
		const colorName = colorCodeToColorName(color);
		return (
			<ColorOptionWrapper onClick={() => onSelectColor(color)} key={`${color}-option`}>
				<ColorOption
					color={color}
					data-testid={`${tokenToHex(color)}-option`}
					aria-label={`${colorName}-color-option`}
				>
					{isSelected && (
						<CheckIcon
							LEGACY_size="small"
							label="selected"
							color={token('color.icon.inverse', N0)}
						/>
					)}
				</ColorOption>
			</ColorOptionWrapper>
		);
	});

	useEffect(() => {
		// Focus color picker button after its poppup closes since the modal loses focus when the color popup is closed.
		// Had to use an id instead of ref to keep track of the button since a ref interferes with the trigger button ref
		// the popup component uses.
		const colorPickerButton = document.getElementById('content-state-color-picker-button');
		if (!isOpen && colorPickerButton) {
			// using a setTimeout since state change/re-render is interfering with timing of things and timeout will help force
			// the focus to happen after the re-render https://salesforce.stackexchange.com/a/366973
			setTimeout(() => {
				colorPickerButton.focus();
			}, 0);
		}
	}, [isOpen]);

	// TODO(brizvash): fix incompatible csstype of emotion and compiled
	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			testId="color-picker-popup"
			placement="bottom-start"
			zIndex={700}
			trigger={({ ...buttonProps }) => (
				<Button
					{...buttonProps}
					id="content-state-color-picker-button"
					testId="color-picker-popup-trigger"
					isSelected={isOpen}
					isDisabled={userOffline}
					onClick={onColorPickerToggle}
					aria-label="color-picker-button"
					iconBefore={
						<ButtonIconsWrapper>
							<SelectedColor
								color={selectedColor || undefined}
								isOpen={isOpen}
								data-testid="selected-color"
							/>
							<Box xcss={chevronDownIconWrapperStyles}>
								<ChevronDownIcon
									color="currentColor"
									LEGACY_size="medium"
									spacing="spacious"
									label=""
								/>
							</Box>
						</ButtonIconsWrapper>
					}
				/>
			)}
			content={() => (
				<DropDownContentWrapper ref={colorPickerComponentRef as any}>
					{statusColors}
				</DropDownContentWrapper>
			)}
		/>
	);
};
